import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import LoadingSpinner from "../../components/LoadingSpinner";

const TemplateEmailHtmlEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Template data to preserve when saving
  const [templateData, setTemplateData] = useState(null);
  
  // State for HTML editor and preview
  const [htmlCode, setHtmlCode] = useState("");
  const [previewMode, setPreviewMode] = useState(null); // null, 'desktop', or 'mobile'

  useEffect(() => {
    if (id) {
      fetchTemplateDetails();
    }
  }, [id]);

  const fetchTemplateDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}settings/template/email/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const data = response.data.data;
      setTemplateData(data);
      // Load existing message (HTML content) or empty string
      setHtmlCode(data.message_html || "");
    } catch (error) {
      console.error("Error fetching template details:", error);
      showToast.error("Failed to load template details");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!templateData) return;
    
    try {
      setSaving(true);
      const formData = new FormData();
      
      // We must send required fields based on CreateOrUpdateTemplateMailDto
      formData.append("templatename", templateData.templatename || "");
      formData.append("subject", templateData.subject || "");
      
      if (templateData.template_id) {
        formData.append("template_id", templateData.template_id);
      }
      
      if (templateData.sender_type !== undefined) {
        formData.append("sender_type", templateData.sender_type);
      }

      if (templateData.attachment_file_name) {
        formData.append("attachment_file_name", templateData.attachment_file_name);
      }
      if (templateData.attachment2_file_name) {
        formData.append("attachment2_file_name", templateData.attachment2_file_name);
      }
      
      // If there are existing attachments, we should probably pass their URLs so they don't get lost
      // But according to the controller, it uploads if new files are present, and Object.assign overwrites.
      // If the frontend form in add.jsx passes attachment_file_name, it's safe. We can also pass existing attachments if needed.
      if (templateData.attachment) {
        formData.append("attachment", templateData.attachment);
      }
      if (templateData.attachment2) {
        formData.append("attachment2", templateData.attachment2);
      }

      // Save the raw HTML code in the message_html field
      formData.append("message_html", htmlCode);

      const response = await axios.post(`${API_URL}settings/template/email/save?id=${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        showToast.success("HTML Template saved successfully");
        navigate("/Settings/TemplateEmail");
      }
    } catch (error) {
      console.error("Error saving HTML template:", error);
      showToast.error(error.response?.data?.message || "Failed to save HTML template");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    if (!htmlCode) {
      showToast.error("Nothing to download");
      return;
    }

    const safeName = (templateData?.templatename || "template")
      .trim()
      .replace(/[^a-z0-9_\-]+/gi, "_");

    const blob = new Blob([htmlCode], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${safeName}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content" className="">
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading border-0 d-flex justify-content-between align-items-center">
            <span className="panel-title">Edit Template HTML: {templateData?.templatename}</span>
            <div className="d-flex gap-2">
              <Link to="/Settings/TemplateEmail" className="btn btn-default">
                Cancel
              </Link>
              <button
                type="button"
                className="btn btn-default"
                onClick={handleDownload}
              >
                <i className="ti-download me-2"></i> Download HTML
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save HTML"}
              </button>
            </div>
          </div>
          
          <div className="panel-body">
            <div className="row">
              {/* HTML Editor Column */}
              <div className="col-md-12">
                <div className="form-group mb-3">
                  <label style={{ fontWeight: 'bold', marginBottom: '8px' }}>HTML Code</label>
                  <textarea
                    className="form-control"
                    style={{ height: "40vh", fontFamily: "monospace", resize: "vertical" }}
                    value={htmlCode}
                    onChange={(e) => setHtmlCode(e.target.value)}
                    placeholder="<html><body>...</body></html>"
                  ></textarea>
                </div>
              </div>
            </div>

            {/* Preview Section */}
            <div className="row mt-3">
              <div className="col-md-12 text-center mb-3">
                <h5 style={{ fontWeight: 'bold' }}>Preview HTML</h5>
                <div className="btn-group mt-2">
                  <button 
                    type="button" 
                    className={`btn ${previewMode === 'desktop' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setPreviewMode(previewMode === 'desktop' ? null : 'desktop')}
                    style={{ padding: '8px 20px' }}
                  >
                    <i className="ti-desktop me-2"></i> Desktop Preview
                  </button>
                  <button 
                    type="button" 
                    className={`btn ${previewMode === 'mobile' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setPreviewMode(previewMode === 'mobile' ? null : 'mobile')}
                    style={{ padding: '8px 20px' }}
                  >
                    <i className="ti-mobile me-2"></i> Mobile Preview
                  </button>
                </div>
              </div>

              {previewMode && (
                <div className="col-md-12">
                  <div 
                    style={{
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      height: "60vh",
                      backgroundColor: "#f9f9f9",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "20px",
                      marginTop: "10px"
                    }}
                  >
                    <iframe
                      title="HTML Preview"
                      srcDoc={(() => {
                        let friendlyHtml = htmlCode || "";
                        // friendlyHtml = friendlyHtml.replace(/\$userinfo->user_whatsapp/g, "+91 9876543210");
                        friendlyHtml = friendlyHtml.replace(/\$userinfo->name/g, "Admin Name");
                        friendlyHtml = friendlyHtml.replace(/\$subscriber->name/g, "Client Name");
                        friendlyHtml = friendlyHtml.replace(/\$subscriber->email/g, "client@example.com");
                        friendlyHtml = friendlyHtml.replace(/\$subscriber->mobile/g, "9876543210");
                        friendlyHtml = friendlyHtml.replace(/\$[a-zA-Z0-9_]+->([a-zA-Z0-9_]+)/g, (match, fieldName) => {
                          const readableName = fieldName.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                          return `[${readableName}]`;
                        });
                        return friendlyHtml;
                      })()}
                      style={{
                        width: previewMode === 'desktop' ? '100%' : '375px',
                        height: '100%',
                        border: '1px solid #ddd',
                        backgroundColor: "#fff",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        borderRadius: previewMode === 'mobile' ? "20px" : "4px",
                        transition: "width 0.3s ease, border-radius 0.3s ease"
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateEmailHtmlEdit;
